
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  Plus,
  Trash2,
  Video,
  Phone,
  Building
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { 
  Card,
  CardContent
} from "@/components/ui/card";
import { 
  TimeBlock, 
  DayScheduleType, 
  WeeklyScheduleType, 
  ConsultationType 
} from './WeeklySchedule';

interface DayScheduleProps {
  dayName: string;
  dayKey: keyof WeeklyScheduleType;
  daySchedule: DayScheduleType;
  setSchedule: React.Dispatch<React.SetStateAction<WeeklyScheduleType>>;
}

// Generate time options in 20-minute intervals
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 6; hour < 22; hour++) {
    for (let min = 0; min < 60; min += 20) {
      const hourStr = hour.toString().padStart(2, '0');
      const minStr = min.toString().padStart(2, '0');
      const time = `${hourStr}:${minStr}`;
      const label = `${hour > 12 ? hour - 12 : hour}:${minStr} ${hour >= 12 ? 'PM' : 'AM'}`;
      options.push({ value: time, label });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const DaySchedule: React.FC<DayScheduleProps> = ({ dayName, dayKey, daySchedule, setSchedule }) => {
  const toggleDayAvailability = () => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        isAvailable: !prev[dayKey].isAvailable
      }
    }));
  };
  
  const addTimeBlock = () => {
    // Default new time block (9 AM - 12 PM)
    const newBlock: TimeBlock = {
      id: uuidv4(),
      startTime: '09:00',
      endTime: '12:00',
      consultationTypes: ['clinic', 'video', 'voice']
    };
    
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        timeBlocks: [...prev[dayKey].timeBlocks, newBlock]
      }
    }));
  };
  
  const removeTimeBlock = (blockId: string) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        timeBlocks: prev[dayKey].timeBlocks.filter(block => block.id !== blockId)
      }
    }));
  };
  
  const updateTimeBlock = (blockId: string, field: keyof TimeBlock, value: any) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        timeBlocks: prev[dayKey].timeBlocks.map(block => {
          if (block.id === blockId) {
            return { ...block, [field]: value };
          }
          return block;
        })
      }
    }));
  };
  
  const getConsultationTypeIcon = (type: ConsultationType) => {
    switch (type) {
      case 'clinic':
        return <Building className="h-4 w-4" />;
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'voice':
        return <Phone className="h-4 w-4" />;
    }
  };
  
  const getConsultationTypeLabel = (type: ConsultationType) => {
    switch (type) {
      case 'clinic':
        return 'In-clinic';
      case 'video':
        return 'Video';
      case 'voice':
        return 'Voice';
    }
  };
  
  return (
    <Card className={`border ${!daySchedule.isAvailable ? 'bg-muted/50' : ''}`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Switch 
              checked={daySchedule.isAvailable} 
              onCheckedChange={toggleDayAvailability}
            />
            <h3 className="text-lg font-medium">{dayName}</h3>
          </div>
          
          {daySchedule.isAvailable && (
            <Button
              variant="outline" 
              size="sm"
              onClick={addTimeBlock}
              className="h-8"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Time Block
            </Button>
          )}
        </div>
        
        {daySchedule.isAvailable ? (
          daySchedule.timeBlocks.length > 0 ? (
            <div className="space-y-4">
              {daySchedule.timeBlocks.map((block) => (
                <div key={block.id} className="border rounded-md p-4 bg-card">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <select 
                          className="border rounded-md px-2 py-1"
                          value={block.startTime}
                          onChange={(e) => updateTimeBlock(block.id, 'startTime', e.target.value)}
                        >
                          {timeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <select 
                          className="border rounded-md px-2 py-1"
                          value={block.endTime}
                          onChange={(e) => updateTimeBlock(block.id, 'endTime', e.target.value)}
                        >
                          {timeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                      <div>
                        <span className="text-sm text-muted-foreground mr-2">Consultation Types:</span>
                        <ToggleGroup 
                          type="multiple" 
                          value={block.consultationTypes} 
                          onValueChange={(value) => {
                            if (value.length > 0) {
                              updateTimeBlock(block.id, 'consultationTypes', value);
                            }
                          }}
                        >
                          <ToggleGroupItem value="clinic" aria-label="In-clinic">
                            <Building className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">In-clinic</span>
                          </ToggleGroupItem>
                          <ToggleGroupItem value="video" aria-label="Video">
                            <Video className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Video</span>
                          </ToggleGroupItem>
                          <ToggleGroupItem value="voice" aria-label="Voice">
                            <Phone className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Voice</span>
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => removeTimeBlock(block.id)}
                        className="h-8 sm:ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-2 flex flex-wrap gap-1">
                    {block.consultationTypes.map((type) => (
                      <Badge key={type} variant="outline" className="text-xs flex items-center">
                        {getConsultationTypeIcon(type)}
                        <span className="ml-1">{getConsultationTypeLabel(type)}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center border border-dashed rounded-md bg-muted/20">
              <p className="text-muted-foreground">No time blocks added yet</p>
              <Button
                variant="outline" 
                size="sm"
                onClick={addTimeBlock}
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Your First Time Block
              </Button>
            </div>
          )
        ) : (
          <div className="py-6 text-center text-muted-foreground">
            Unavailable on {dayName}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DaySchedule;
